import { css } from '@emotion/react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { useBooking } from '@/booking';
import { useDebounce } from '@/common/hooks';
import { useCurrencyPreview } from '@/currency-preview';
import { useHotel } from '@/hotel';
import { Trans } from '@/i18n';
import { useTheme } from '@/ui/theme';
import { BodyCopy } from '@/ui/typography';

import { useUpdateBookingAddOn } from '../hooks';
import type { IBookingAddOnInstance } from '../models';
import { AddOnCard } from './AddOnCard.component';

export const AddOnWithRocker = (props: {
  addOn: IBookingAddOnInstance;
  bookingId: string;
  hotelReferenceId: string;
  currentAddOns: Pick<IBookingAddOnInstance, 'id' | 'requestedQuantity'>[];
  isLoading: boolean;
  isDisabled: boolean;
  updateAddOn: ReturnType<typeof useUpdateBookingAddOn>['updateAddOn'];
}) => {
  const {
    componentProperties: { addOnCard: addOnCardStyles },
  } = useTheme();

  const bookingAPIValue =
    props.currentAddOns.find((a) => a.id === props.addOn.id)
      ?.requestedQuantity ?? 0;

  const { watch, register, setValue } = useForm<{
    count: number;
  }>({
    defaultValues: {
      count: bookingAPIValue ?? 0, // default value
    },
  });

  const booking = useBooking({
    bookingId: props.bookingId,
    hotelSlug: props.hotelReferenceId,
  });
  const hotel = useHotel(props.hotelReferenceId);

  const quantity = watch('count');

  const localisedAddOnPrice = useCurrencyPreview({
    amount: props.addOn.cost.amountAfterTax,
  });

  const debouncedQuantity = useDebounce(
    React.useMemo(() => quantity, [quantity]),
    500
  );

  const descriptionId = `${props.addOn.id}-description`;

  useUpdateEffect(() => {
    // Prevent circular trigger when quantity is reverted due to failure
    if (debouncedQuantity !== bookingAPIValue && hotel && booking) {
      props.updateAddOn(
        {
          id: props.addOn.id,
          quantity: debouncedQuantity,
          bookingId: props.bookingId,
          hotelReferenceId: props.hotelReferenceId,
          previousAddOns: props.currentAddOns,
        },
        {
          onSettled: (response) => {
            // Revert rocker state back if failed to update add-on
            if (!response || response.status !== 'CREATED') {
              setValue('count', bookingAPIValue);
            }
          },
        }
      );
    }
  }, [debouncedQuantity]);

  // Sync up booking state if it's different.
  // For instance if add-on was added on a different tab.
  useEffect(() => {
    if (!props.isLoading && bookingAPIValue !== quantity) {
      setValue('count', bookingAPIValue);
    }
  }, [bookingAPIValue]);

  return (
    <AddOnCard
      isAdded={props.isLoading ? false : quantity > 0}
      localisedAddOnPrice={localisedAddOnPrice}
      descriptionId={descriptionId}
      {...props.addOn}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <BodyCopy css={addOnCardStyles?.ctaLabel}>
          <Trans
            defaults={props.addOn.controlLabel}
            values={{ price: localisedAddOnPrice }}
          />
        </BodyCopy>
        <AddOnCard.Rocker
          {...register('count', { valueAsNumber: true })}
          count={quantity}
          onDec={() => setValue('count', quantity - 1)}
          onInc={() => setValue('count', quantity + 1)}
          isLoading={props.isLoading || props.isDisabled}
          aria-label={`${quantity} ${props.addOn.name}`}
          aria-describedby={descriptionId}
        />
      </div>
    </AddOnCard>
  );
};
